/* Right-click a booking — the line in today's book, or the chip in Due now —
 * to say whether the client asked for that technician by name.
 *
 * It exists because nothing in the booking system's payload answers that
 * question: Mangomint puts exactly one assigned tech on every booked service
 * whether the client asked for them or the desk simply put them there. So the
 * board is told, one booking at a time, at the moment somebody actually knows.
 *
 * Anchored at the pointer rather than centred, because it acts on the thing you
 * clicked and there may be twenty of them on screen.
 */
import * as Vue from 'vue';
import { store } from '../store.js';
import { useMenuFocus } from '../menu-focus.js';

var MENU_W = 244;
var MENU_H = 190;   // enough to keep the whole card on screen near an edge
var EDGE = 8;

export const ApptMenu = {
  name: 'ApptMenu',
  setup: function () {
    var target = store.apptMenuTarget;
    var focus = useMenuFocus(Vue.computed(function () { return !!target.value; }));

    var pos = Vue.computed(function () {
      var m = store.ui.apptMenu;
      if (!m) return { left: '0px', top: '0px' };
      var w = window.innerWidth, h = window.innerHeight;
      return {
        left: Math.max(EDGE, Math.min(m.x, w - MENU_W - EDGE)) + 'px',
        top: Math.max(EDGE, Math.min(m.y, h - MENU_H - EDGE)) + 'px'
      };
    });

    // Whoever the book has on it, as a roster name rather than the provider's
    // "Zenny T." label — the mark is for a person on this board.
    var tech = Vue.computed(function () {
      return target.value ? store.bookedStaffFor(target.value.svc) : null;
    });

    var marked = Vue.computed(function () {
      return !!(target.value && store.isApptRequested(target.value.svc));
    });

    // A booking nobody is named on cannot be a request for anybody. Marking it
    // would record a request for no one, which is not a fact about anything.
    var canMark = Vue.computed(function () { return !!tech.value; });

    // Whole sentences rather than text stitched around {{ }}: the compiler
    // eats the whitespace either side of an interpolation and the seams show.
    var label = Vue.computed(function () {
      if (!tech.value) return 'Mark as a client request';
      return marked.value
        ? 'Not a request for ' + tech.value.name
        : 'Client asked for ' + tech.value.name;
    });

    var hint = Vue.computed(function () {
      if (!target.value) return '';
      if (!tech.value) {
        return 'Nobody is on this booking yet, so there is nobody to have ' +
          'been asked for. Assign it first.';
      }
      if (marked.value) {
        return 'Marked as a request for ' + tech.value.name + '. The square ' +
          'carries a star when it starts, and nothing else goes on that ' +
          'column while it runs.';
      }
      return store.schedulingName.value + ' does not say whether a client ' +
        'asked for anyone, so the board only knows what it is told here.';
    });

    function toggle() {
      if (!target.value || !canMark.value) return;
      store.toggleApptRequest(target.value.appt, target.value.svc);
      store.closeApptMenu();
    }

    return {
      target: target, pos: pos, tech: tech, marked: marked, canMark: canMark,
      label: label, hint: hint, toggle: toggle,
      formatClock: store.formatClock,
      menuEl: focus.menuEl,
      onMenuKey: focus.onMenuKey,
      close: store.closeApptMenu
    };
  },
  template: [
    '<div v-if="target" class="ctx-scrim" @click.self="close" @contextmenu.prevent="close">',
    '  <div class="ctx ctx--appt" :style="pos" role="menu"',
    '       ref="menuEl" @keydown="onMenuKey">',
    '    <div class="ctx__head">',
    '      <strong>{{ target.appt.clientName }}<client-note :client-id="target.appt.clientId" /></strong>',
    '      <span>{{ formatClock(target.svc.startsAt) }} &middot; {{ target.svc.name }}</span>',
    '    </div>',
    '',
    '    <button type="button" class="ctx__item" role="menuitemcheckbox"',
    '      :class="{ \'is-on\': marked }" :disabled="!canMark"',
    '      :aria-checked="marked ? \'true\' : \'false\'" @click="toggle">',
    '      <span class="ctx__box ctx__box--star" aria-hidden="true">{{ marked ? "\\u2605" : "\\u2606" }}</span>',
    '      <span class="ctx__label-text">{{ label }}</span>',
    '    </button>',
    '',
    '    <p class="ctx__hint">{{ hint }}</p>',
    '  </div>',
    '</div>'
  ].join('\n')
};
